/**
 * CaretOverlay — 원격 사용자 커서를 DOM overlay로 렌더
 *
 * 각 원격 사용자마다 색상별 세로 caret 바 + 이름 label을 표시한다.
 * 5분 idle 시 자동 hide.
 */

const IDLE_HIDE_MS = 5 * 60 * 1000 // 5분

export class CaretOverlay {
  /**
   * @param {Object} options
   * @param {HTMLElement} options.container - 에디터 컨테이너
   */
  constructor({ container }) {
    this.container = container
    this.carets = new Map() // clientId → { el, labelEl, timer, lastUpdate }

    // overlay 컨테이너 생성
    this.overlayEl = document.createElement('div')
    this.overlayEl.className = 'collab-caret-overlay'
    this.overlayEl.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:50;'
    this.container.style.position = this.container.style.position || 'relative'
    this.container.appendChild(this.overlayEl)
  }

  /**
   * 원격 caret 위치 업데이트
   *
   * @param {number} clientId
   * @param {Object} options
   * @param {DOMRect} options.rect - caret 화면 좌표
   * @param {string} options.name - 사용자 이름
   * @param {string} options.color - 색상 (CSS)
   * @param {HTMLElement} options.blockEl - 해당 블록 DOM 요소
   */
  updateCaret(clientId, { rect, name, color, blockEl }) {
    let caret = this.carets.get(clientId)

    if (!caret) {
      caret = this._createCaretElement(clientId, name, color)
      this.carets.set(clientId, caret)
    }

    // 컨테이너 기준 상대 좌표로 변환 (스크롤 오프셋 포함)
    const containerRect = this.container.getBoundingClientRect()
    const x = rect.left - containerRect.left + this.container.scrollLeft
    const y = rect.top - containerRect.top + this.container.scrollTop
    const height = rect.height || 20

    // 비정상 좌표면 숨기기 (음수이거나 컨테이너 밖)
    if (x < -10 || y < -10 || x > containerRect.width + 10) {
      caret.el.style.display = 'none'
      return
    }

    caret.el.style.transform = `translate(${x}px, ${y}px)`
    caret.el.style.height = `${height}px`
    caret.el.style.display = 'block'

    // label 위치
    caret.labelEl.style.transform = `translateY(-100%)`

    // 이름/색상 업데이트
    caret.labelEl.textContent = name
    caret.el.style.backgroundColor = color
    caret.labelEl.style.backgroundColor = color

    // idle timer 리셋
    if (caret.timer) clearTimeout(caret.timer)
    caret.timer = setTimeout(() => {
      caret.el.style.display = 'none'
    }, IDLE_HIDE_MS)
  }

  /**
   * 원격 caret 제거
   */
  removeCaret(clientId) {
    const caret = this.carets.get(clientId)
    if (!caret) return

    if (caret.timer) clearTimeout(caret.timer)
    caret.el.remove()
    this.carets.delete(clientId)
  }

  destroy() {
    for (const [clientId, caret] of this.carets) {
      if (caret.timer) clearTimeout(caret.timer)
      caret.el.remove()
    }
    this.carets.clear()
    this.overlayEl.remove()
  }

  // --- Private ---

  _createCaretElement(clientId, name, color) {
    const el = document.createElement('div')
    el.className = 'collab-caret'
    el.dataset.clientId = clientId
    el.style.cssText = `
      position: absolute;
      width: 2px;
      background-color: ${color};
      transition: transform 0.15s ease;
      pointer-events: none;
    `

    const labelEl = document.createElement('div')
    labelEl.className = 'collab-caret-label'
    labelEl.textContent = name
    labelEl.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      background-color: ${color};
      color: white;
      font-size: 11px;
      line-height: 16px;
      padding: 1px 6px;
      border-radius: 3px 3px 3px 0;
      white-space: nowrap;
      pointer-events: none;
      user-select: none;
    `

    el.appendChild(labelEl)
    this.overlayEl.appendChild(el)

    return { el, labelEl, timer: null }
  }
}
